import React, { useContext, useEffect } from "react";
import Select from "react-select";

import "./index.css";

import {
  getSementesCultivar,
  getSementesProposito,
} from "../../../../../../Services/programation.service";
import { useQuery } from "@tanstack/react-query";
import { ConfiguracaoGlebaContext } from "../../../../../../contexts/ConfiguracaoGlebaContext";

function Sementes() {
  const { configuracaoGleba, setConfiguracaoGleba, activeCulturaIndex, setProgramacaoAlterada } = useContext(
    ConfiguracaoGlebaContext
  );

  // Cultura da aba ativa — usada na busca E na chave da query (senão, ao trocar
  // de aba, o cache serve as cultivares da cultura ERRADA até o refetch).
  const culturaIdAtivo =
    configuracaoGleba?.culturas?.[activeCulturaIndex]?.culturaId;

  const sementesConfigurationFetch = async () => {
    const {
      res: { data: propositosData },
    } = await getSementesProposito();
    const {
      res: { data: cultivaresData },
    } = await getSementesCultivar(culturaIdAtivo);
    return {
      propositos: propositosData
        .filter((item) => !item.excluido)
        .map((item) => ({
          label: item.nome,
          value: item.id,
        })),
      cultivares: cultivaresData
        .filter((item) => !item.excluido)
        .map((item) => ({
          label: item.nome,
          value: item.id,
          pms: item.pms,
        })),
      sementePropria: [
        {
          label: "Sim",
          value: true,
        },
        {
          label: "Não",
          value: false,
        },
      ],
      taxaVariavel: [
        {
          label: "Sim",
          value: true,
        },
        {
          label: "Não",
          value: false,
        },
      ],
      sementePorSaca: [
        {
          label: "60mil",
          value: 60000,
        },
        {
          label: "80mil",
          value: 80000,
        },
      ],

      sementePorBag: [
        {
          label: "4m",
          value: 4000000,
        },
        {
          label: "5m",
          value: 5000000,
        },
      ],

    };
  };

  const configOptionsQuery = useQuery(
    ["configOptionsQuery", culturaIdAtivo],
    sementesConfigurationFetch,
    {
      propositos: [],
      cultivares: [],
      sementePropria: [
        {
          label: "Sim",
          value: true,
        },
        {
          label: "Não",
          value: false,
        },
      ],



    }
  );

  const handleSelectChange = (name) => {
    return (option) => {
      setProgramacaoAlterada(true);
      // Atualiza SEM mutar o estado (mutação direta corrompe o prevState
      // de outras atualizações no mesmo lote).
      setConfiguracaoGleba((prevState) => ({
        ...prevState,
        culturas: (prevState.culturas || []).map((cultura, i) => {
          if (i !== activeCulturaIndex) return cultura;
          let bloco = { ...cultura, [name]: option.value };
          if (name === "cultivarId") {
            bloco = {
              ...bloco,
              pms: option.pms != null ? option.pms : bloco.pms,
              cultivar: {
                ...cultura.cultivar,
                nome: option.label,
                id: option.value,
                pms: option.pms,
              },
            };
          }
          if (name === "propositoId") {
            bloco = {
              ...bloco,
              proposito: {
                ...cultura.proposito,
                nome: option.label,
                id: option.value,
              },
            };
          }
          return bloco;
        }),
      }));
    };
  };

  const handleInputChange = (name) => {
    return (e) => {
      setProgramacaoAlterada(true);
      let value = e.target.value;

      if (name === "dataRealizacao" && !value.trim()) {
        value = "";
      }

      // Atualiza SEM mutar o estado
      setConfiguracaoGleba((prevState) => ({
        ...prevState,
        culturas: (prevState.culturas || []).map((cultura, i) => {
          if (i !== activeCulturaIndex) return cultura;
          const blocoAtual = { ...cultura, [name]: value };

          // Ao mudar a Area da cultivar, replica a mesma area para TODOS os insumos do bloco
          // (cada insumo cobre a area inteira da cultivar).
          if (name === "area") {
            const replicarArea = (lista) =>
              (lista || []).map((item) => ({ ...item, area: value }));
            blocoAtual.adubacao = replicarArea(blocoAtual.adubacao);
            blocoAtual.adubacaoFolhar = replicarArea(blocoAtual.adubacaoFolhar);
            blocoAtual.herbicida = replicarArea(blocoAtual.herbicida);
            blocoAtual.inseticida = replicarArea(blocoAtual.inseticida);
            blocoAtual.fungicida = replicarArea(blocoAtual.fungicida);
            blocoAtual.outrosProdutos = replicarArea(blocoAtual.outrosProdutos);
            blocoAtual.tratamentoSementes = replicarArea(
              blocoAtual.tratamentoSementes
            );
          }
          return blocoAtual;
        }),
      }));
    };
  };

  function calcularCamposInternos(rowData, configuracaoGleba) {
    const area = rowData.area || 0;
    const quantidade = rowData.quantidade || 0;
    const pms = rowData.pms || 0;
    const sementePorSaca = rowData.sementePorSaca || 60000;
    // 5 milhões = mesmo default usado no restante da tela (era 4M aqui — divergia)
    const sementePorBag = rowData.sementePorBag || 5000000;

    // Usa o tipo de semente da PRÓPRIA cultura deste bloco (rowData), não da aba ativa —
    // senão, num talhão com 2 cultivares de tipos diferentes, um bloco pega o tipo do outro
    // e recalcula o kgHa errado (perdendo o valor copiado na clonagem).
    const isPms = rowData.cultura?.isPms;
    const isSementePorSaca = rowData.cultura?.isSementePorSaca;
    const isSementePorBag = rowData.cultura?.isSementePorBag;

    let kgHa = rowData.kgHa;
    let quantidadeTotal = 0;

    if (isPms && !isSementePorSaca && !isSementePorBag) {
      kgHa = parseFloat(((quantidade * 10000 * pms) / 1000).toFixed(2));
      quantidadeTotal = parseFloat((area * kgHa).toFixed(2));
    }

    if (!isPms && !isSementePorBag && !isSementePorSaca) {
      kgHa = rowData.kgHa;
      quantidadeTotal = parseFloat((area * kgHa).toFixed(2));
    }

    if (isSementePorSaca && !isSementePorBag) {
      quantidadeTotal = parseFloat(
        ((area * (quantidade * 10000)) / sementePorSaca).toFixed(2)
      );
    }

    if (isSementePorBag) {
      // ✅ CALCULA kgHa mesmo no modo BAG
      kgHa = parseFloat(((quantidade * 10000 * pms) / 1000).toFixed(2));
      quantidadeTotal = parseFloat((area * kgHa).toFixed(2));
    }

    const quantidadeTotalEmSementes = parseFloat((area * quantidade * 10000).toFixed(0));

    return {
      kgHa,
      quantidadeTotal,
      quantidadeTotalEmSementes,
    };
  }

  useEffect(() => {
    setConfiguracaoGleba((prev) => {
      const culturasAtualizadas = (prev.culturas || []).map((cultura) => {
        const sementePorBagDefault = cultura.sementePorBag ?? 5000000;
        const sementePorSacaDefault = cultura.sementePorSaca ?? 60000;

        const calculo = calcularCamposInternos(
          {
            ...cultura,
            sementePorBag: sementePorBagDefault,
            sementePorSaca: sementePorSacaDefault,
          },
          prev
        );

        return {
          ...cultura,
          sementePorBag: sementePorBagDefault,
          sementePorSaca: sementePorSacaDefault,
          kgHa: calculo.kgHa,
          quantidadeTotalCalculada: calculo.quantidadeTotal,
          quantidadeTotalEmSementes: calculo.quantidadeTotalEmSementes,
        };
      });


      return {
        ...prev,
        culturas: culturasAtualizadas,
      };
    });
  }, [
    configuracaoGleba.defaultValues.area,
    configuracaoGleba.culturas[activeCulturaIndex]?.quantidade,
    configuracaoGleba.culturas[activeCulturaIndex]?.pms,
    configuracaoGleba.culturas[activeCulturaIndex]?.area,
    configuracaoGleba.culturas[activeCulturaIndex]?.sementePorBag,
    // milho (saca): mudar semente/saca precisa recalcular o total na hora
    configuracaoGleba.culturas[activeCulturaIndex]?.sementePorSaca,
    configuracaoGleba.culturas[activeCulturaIndex]?.kgHa,
  ]);

  return (
    <div className="table-responsive">
      <div className="title-card">SEMENTE</div>

      {configuracaoGleba?.culturas?.length !== 0 ? (
        <table className="table tabela-original">
          <tbody style={{ width: "100%", marginBottom: "0px" }}>
            {(configuracaoGleba?.culturas?.[activeCulturaIndex]
              ? [{ rowData: configuracaoGleba.culturas[activeCulturaIndex], index: activeCulturaIndex }]
              : []
            ).map(({ rowData, index }) => (

              <tr
                style={{
                  display: "flex",
                  width: "max-content",
                  margin: "10px",
                  borderRadius: "6px",
                  padding: "5px",
                  background: "rgba(1, 104, 32, 0.1)",
                }}
                key={index}
              >
                {configuracaoGleba.culturas[activeCulturaIndex]?.cultura?.isSementePorBag ? (
                  //SEMENTE POR BAG
                  <>
                    <div className="column-configs-table">
                      <th className="correct-inline">Cultivar</th>
                      <td>
                        <Select
                          menuPosition="fixed"
                          value={{
                            label: rowData?.cultivar?.nome,
                            value: rowData?.cultivar?.id,
                          }}
                          onChange={handleSelectChange("cultivarId")}
                          options={configOptionsQuery?.data?.cultivares}
                        />
                      </td>
                    </div>

                    <div className="column-configs-table">
                      <th className="correct-inline">Propósito</th>
                      <td>
                        <Select
                          menuPosition="fixed"
                          value={{
                            label: rowData?.proposito?.nome,
                            value: rowData?.proposito?.id,
                          }}
                          onChange={handleSelectChange("propositoId")}
                          options={configOptionsQuery?.data?.propositos}
                        />
                      </td>
                    </div>


                    <div className="column-configs-table">
                      <th className="correct-inline">Semente Própria</th>
                      <td>
                        <Select
                          menuPosition="fixed"
                          defaultValue={{
                            label: rowData.sementePropria ? "Sim" : "Não",
                            value: rowData.sementePropria,
                          }}
                          onChange={handleSelectChange("sementePropria")}
                          options={configOptionsQuery?.data?.sementePropria}
                        />
                      </td>
                    </div>

                    <div className="column-configs-table">
                      <th className="correct-inline">Quantidade/m²</th>
                      <td>
                        <input
                          className="input-atributo"
                          type="number"
                          value={rowData.quantidade}
                          onChange={handleInputChange("quantidade")}
                        />
                      </td>
                    </div>

                    <div className="column-configs-table">
                      <th className="correct-inline">Plantas/ha</th>
                      <td>
                        <input
                          className="input-atributo"
                          type="number"
                          disabled={true}
                          style={{
                            background: "rgba(1, 104, 32, 0.1)",
                            fontWeight: "bold",
                            color: "black",
                            fontSize: "15px",
                            textAlign: "center",
                          }}
                          value={rowData.quantidade * 10000}
                        />
                      </td>
                    </div>

                    <div className="column-configs-table">
                      <th className="correct-inline">Semente/bag</th>
                      <td>
                        <Select
                          menuPosition="fixed"
                          value={{
                            label: rowData.sementePorBag,
                            value: rowData.sementePorBag,
                          }}
                          onChange={handleSelectChange("sementePorBag")}
                          options={configOptionsQuery?.data?.sementePorBag}
                        />
                      </td>
                    </div>

                    <div className="column-configs-table">
                      <th className="correct-inline">bag/ha</th>
                      <td>
                        <input
                          className="input-atributo"
                          type="number"
                          disabled={true}
                          style={{
                            background: "rgba(1, 104, 32, 0.1)",
                            fontWeight: "bold",
                            color: "black",
                            fontSize: "15px",
                            textAlign: "center",
                          }}
                          value={parseFloat(
                            (rowData.quantidade * 10000) / (rowData.sementePorBag || 5000000)
                          ).toFixed(3)}
                        />
                      </td>
                    </div>

                    <div className="column-configs-table">
                      <th className="correct-inline">PMS</th>
                      <td>
                        <input
                          className="input-atributo"
                          type="number"
                          value={rowData.pms}
                          onChange={handleInputChange("pms")}
                        />
                      </td>
                    </div>


                    <div className="column-configs-table">
                      <th className="correct-inline">Planta por metro Lin.</th>
                      <td>
                        <input
                          className="input-atributo"
                          type="number"
                          disabled={true}
                          style={{
                            background: "rgba(1, 104, 32, 0.1)",
                            fontWeight: "bold",
                            color: "black",
                            fontSize: "15px",
                            textAlign: "center",
                          }}
                          value={parseFloat(rowData.quantidade / (1 / rowData.espaco)).toFixed(2)}
                        />
                      </td>
                    </div>

                    <div className="column-configs-table">
                      <th className="correct-inline">Espaçamento</th>
                      <td>
                        <input
                          className="input-atributo"
                          type="number"
                          value={rowData.espaco}
                          onChange={handleInputChange("espaco")}
                          style={{
                            fontWeight: "bold",
                            color: "black",
                            fontSize: "15px",
                            textAlign: "center",
                          }}
                        />
                      </td>
                    </div>

                    <div className="column-configs-table">
                      <th className="correct-inline">Área (ha)</th>
                      <td>
                        <input
                          className="input-atributo"
                          type="number"
                          value={
                            rowData.area
                              ? rowData.area
                              : ""
                          }
                          onChange={handleInputChange("area")}
                          style={{
                            fontWeight: "bold",
                            color: "black",
                            fontSize: "15px",
                            textAlign: "center",
                          }}
                        />
                      </td>
                    </div>

                    <div className="column-configs-table">
                      <th className="correct-inline">Quantidade total em sementes</th>
                      <td>
                        <input
                          className="input-atributo"
                          type="number"
                          disabled={true}
                          style={{
                            background: "rgba(1, 104, 32, 0.1)",
                            fontWeight: "bold",
                            color: "black",
                            fontSize: "15px",
                            textAlign: "center",
                          }}
                          value={parseFloat(
                            (rowData.area || 0) *
                            rowData.quantidade *
                            10000
                          ).toFixed(2)}
                        />
                      </td>
                    </div>

                    <div className="column-configs-table">
                      <th className="correct-inline">Quantidade total em bags</th>
                      <td>
                        <input
                          className="input-atributo"
                          type="number"
                          disabled={true}
                          style={{
                            background: "rgba(1, 104, 32, 0.1)",
                            fontWeight: "bold",
                            color: "black",
                            fontSize: "15px",
                            textAlign: "center",
                          }}
                          value={parseFloat(
                            ((rowData.area || 0) *
                              (rowData.quantidade * 10000)) /
                            (rowData.sementePorBag || 5000000)
                          ).toFixed(2)}

                        />
                      </td>
                    </div>

                    <div className="column-configs-table">
                      <th className="correct-inline">Data de Plantio</th>
                      <td>
                        <input
                          className="input-atributo"
                          type="text"
                          value={rowData.dataRealizacao}
                          onChange={handleInputChange("dataRealizacao")}
                          style={{
                            fontWeight: "bold",
                            color: "black",
                            fontSize: "15px",
                            textAlign: "center",
                          }}
                        />
                      </td>
                    </div>

                  </>
                ) : (
                  <>
                    <div className="column-configs-table">
                      <th className="correct-inline">Cultivar</th>
                      <td>
                        <Select
                          menuPosition="fixed"
                          value={{
                            label: rowData?.cultivar?.nome,
                            value: rowData?.cultivar?.id,
                          }}
                          onChange={handleSelectChange("cultivarId")}
                          options={configOptionsQuery?.data?.cultivares}
                        />
                      </td>
                    </div>

                    <div className="column-configs-table">
                      <th className="correct-inline">Propósito</th>
                      <td>
                        <Select
                          menuPosition="fixed"
                          value={{
                            label: rowData?.proposito?.nome,
                            value: rowData?.proposito?.id,
                          }}
                          onChange={handleSelectChange("propositoId")}
                          // onChange={setSelectedOption}
                          options={configOptionsQuery?.data?.propositos}
                        />
                      </td>
                    </div>
                    <div className="column-configs-table">
                      <th className="correct-inline">Semente Própria</th>
                      { }
                      <td>
                        <Select
                          menuPosition="fixed"
                          defaultValue={{
                            label: rowData.sementePropria ? "Sim" : "Não",
                            value: rowData.sementePropria,
                          }}
                          onChange={handleSelectChange("sementePropria")}
                          options={configOptionsQuery?.data?.sementePropria}
                        />
                      </td>
                    </div>
                    {configuracaoGleba.culturas[activeCulturaIndex]?.cultura?.isSementePorSaca ===
                      false &&
                      configuracaoGleba.culturas[activeCulturaIndex]?.cultura?.isPms === false ? (
                      ""
                    ) : (
                      <div className="column-configs-table">
                        <th className="correct-inline">Quantidade/m²</th>
                        <td>
                          <input
                            className="input-atributo"
                            type="number"
                            value={rowData.quantidade}
                            onChange={handleInputChange("quantidade")}
                          />
                        </td>
                      </div>
                    )}
                    {configuracaoGleba.culturas[activeCulturaIndex]?.cultura?.isSementePorSaca ===
                      false ? (
                      configuracaoGleba.culturas[activeCulturaIndex]?.cultura?.isPms ? (
                        <div className="column-configs-table">
                          <th className="correct-inline">Kg/ha</th>
                          <td>
                            <input
                              style={{
                                background: "rgba(1, 104, 32, 0.1)",
                                fontWeight: "bold",
                                color: "black",
                                fontSize: "15px",
                                textAlign: "center",
                              }}
                              className="input-atributo"
                              type="number"
                              disabled={true}
                              value={parseFloat(
                                (rowData.quantidade * 10000 * rowData.pms) / 1000
                              ).toFixed(2)}
                              onChange={handleInputChange("kgHa")}
                            />
                          </td>
                        </div>
                      ) : (
                        <div className="column-configs-table">
                          <th className="correct-inline">Kg/ha</th>
                          <td>
                            <input
                              className="input-atributo"
                              type="number"
                              value={rowData.kgHa}
                              onChange={handleInputChange("kgHa")}
                            />
                          </td>
                        </div>
                      )
                    ) : (
                      ""
                    )}

                    {configuracaoGleba.culturas[activeCulturaIndex]?.cultura?.isSementePorSaca ? (
                      <>
                        <div className="column-configs-table">
                          <th className="correct-inline">Plantas/ha</th>
                          <td>
                            <input
                              style={{
                                background: "rgba(1, 104, 32, 0.1)",
                                fontWeight: "bold",
                                color: "black",
                                fontSize: "15px",
                                textAlign: "center",
                              }}
                              className="input-atributo"
                              type="number"
                              disabled="true"
                              value={rowData.quantidade * 10000}
                            />
                          </td>
                        </div>
                        <div className="column-configs-table">
                          <th className="correct-inline">Semente/saca</th>
                          <td>
                            <Select
                              menuPosition="fixed"
                              value={{
                                label: rowData.sementePorSaca,
                                //   ? rowData.sementePorSaca === 60000
                                //     ? "60mil"
                                //     : "80mil"
                                //   : "60mil",
                                value: 60000,
                              }}
                              onChange={handleSelectChange("sementePorSaca")}
                              options={configOptionsQuery?.data?.sementePorSaca}
                            />
                          </td>
                        </div>
                        <div className="column-configs-table">
                          <th className="correct-inline">Saca/ha</th>
                          <td>
                            <input
                              style={{
                                background: "rgba(1, 104, 32, 0.1)",
                                fontWeight: "bold",
                                color: "black",
                                fontSize: "15px",
                                textAlign: "center",
                              }}
                              className="input-atributo"
                              disabled="true"
                              type="number"
                              value={parseFloat(
                                (rowData.quantidade * 10000) /
                                (rowData.sementePorSaca || 60000)
                              ).toFixed(2)}
                              onChange={handleInputChange("sacaPorHa")}
                            />
                          </td>
                        </div>
                      </>
                    ) : (
                      ""
                    )}
                    {configuracaoGleba.culturas[activeCulturaIndex]?.cultura?.isPms ? (
                      <div className="column-configs-table">
                        <th className="correct-inline">PMS</th>
                        <td>
                          <input
                            className="input-atributo"
                            type="number"
                            value={rowData.pms}
                            onChange={handleInputChange("pms")}
                          />
                        </td>
                      </div>
                    ) : (
                      ""
                    )}

                    {configuracaoGleba.culturas[activeCulturaIndex]?.cultura?.isPms ||
                      configuracaoGleba.culturas[activeCulturaIndex]?.cultura?.isSementePorSaca ? (
                      <div className="column-configs-table">
                        <th className="correct-inline">Planta por metro Lin.</th>
                        <td>
                          <input
                            className="input-atributo"
                            type="number"
                            disabled={true}
                            value={parseFloat(
                              rowData.quantidade / (1 / rowData.espaco)
                            ).toFixed(2)}
                            style={{
                              background: "rgba(1, 104, 32, 0.1)",
                              fontWeight: "bold",
                              color: "black",
                              fontSize: "15px",
                              textAlign: "center",
                            }}
                            onChange={handleInputChange("area")}
                          />
                        </td>
                      </div>
                    ) : (
                      ""
                    )}

                    <div className="column-configs-table">
                      <th className="correct-inline">Espaçamento</th>
                      <td>
                        <input
                          className="input-atributo"
                          type="number"
                          value={rowData.espaco}
                          onChange={handleInputChange("espaco")}
                          disabled={false}
                          style={{
                            // background: "rgba(1, 104, 32, 0.1)",
                            fontWeight: "bold",
                            color: "black",
                            fontSize: "15px",
                            textAlign: "center",
                          }}
                        />
                      </td>
                    </div>
                    <div className="column-configs-table">
                      <th className="correct-inline">Área (ha)</th>
                      <td>
                        <input
                          className="input-atributo"
                          type="number"
                          disabled={false}
                          value={
                            rowData.area
                              ? rowData.area
                              : ""
                          }
                          // value={
                          //   rowData.area
                          //     ? rowData.area
                          //     : configuracaoGleba.gleba.area
                          // }
                          style={{
                            // background: "rgba(1, 104, 32, 0.1)",
                            fontWeight: "bold",
                            color: "black",
                            fontSize: "15px",
                            textAlign: "center",
                          }}
                          onChange={handleInputChange("area")}
                        />
                      </td>
                    </div>
                    <div className="column-configs-table">
                      <th className="correct-inline">Quantidade total </th>
                      <td>
                        {configuracaoGleba.culturas[activeCulturaIndex]?.cultura
                          ?.isSementePorSaca !== false ? (
                          <input
                            style={{
                              background: "rgba(1, 104, 32, 0.1)",
                              fontWeight: "bold",
                              color: "black",
                              fontSize: "15px",
                              textAlign: "center",
                            }}
                            className="input-atributo"
                            type="number"
                            disabled="true"
                            value={parseFloat(
                              rowData.area
                                ? rowData.area *
                                ((rowData.quantidade * 10000) /
                                  (rowData.sementePorSaca || 60000))
                                : 0 *
                                ((rowData.quantidade * 10000) /
                                  (rowData.sementePorSaca || 60000))
                            ).toFixed(2)}
                          />
                        ) : (
                          ""
                        )}
                        {configuracaoGleba.culturas[activeCulturaIndex]?.cultura
                          ?.isSementePorSaca === false &&
                          configuracaoGleba.culturas[activeCulturaIndex]?.cultura?.isPms === false ? (
                          <input
                            style={{
                              background: "rgba(1, 104, 32, 0.1)",
                              fontWeight: "bold",
                              color: "black",
                              fontSize: "15px",
                              textAlign: "center",
                            }}
                            className="input-atributo"
                            type="number"
                            disabled="true"
                            value={parseFloat(
                              rowData.area
                                ? rowData.area * rowData.kgHa
                                : 0 *
                                rowData.kgHa
                            ).toFixed(2)}
                          />
                        ) : (
                          ""
                        )}
                        {configuracaoGleba.culturas[activeCulturaIndex]?.cultura?.isPms !== false ? (
                          <input
                            style={{
                              background: "rgba(1, 104, 32, 0.1)",
                              fontWeight: "bold",
                              color: "black",
                              fontSize: "15px",
                              textAlign: "center",
                            }}
                            className="input-atributo"
                            type="number"
                            disabled="true"
                            value={parseFloat(
                              rowData.area
                                ? (rowData.area *
                                  (rowData.quantidade * 10000 * rowData.pms)) /
                                1000
                                : (0 *
                                  (rowData.quantidade * 10000 * rowData.pms)) /
                                1000
                            ).toFixed(2)}
                          />
                        ) : (
                          ""
                        )}
                      </td>
                    </div>
                    <div className="column-configs-table">
                      <th className="correct-inline">Data de Plantio</th>
                      <td>
                        <input
                          className="input-atributo"
                          type="text"
                          value={rowData.dataRealizacao ?? ""}
                          onChange={handleInputChange("dataRealizacao")}
                          disabled={false}
                          style={{
                            fontWeight: "bold",
                            color: "black",
                            fontSize: "15px",
                            textAlign: "center",
                          }}
                        />

                      </td>
                    </div>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <div className="no-data-info">Você não possui dados cadastrados.</div>
      )}
    </div>
  );
}

export default Sementes;
